import React, { FC, useCallback, useState } from 'react';

import { BodyText1, Checkbox, FlexColumn, RadioSelect } from '../';
import { styled } from '../../theme';
import { SurveyQuestion as SurveyQuestionType } from '../../types';
import InputComment from '../InputComment';

type Answer = {
    selected?: number | number[];
    comment?: string;
    comments?: Record<number, string>;
};

type Props = {
    question: SurveyQuestionType;
    answer: Answer;
    onChange: (answer: Answer) => void;
};

const labelStyles = {
    '> label': {
        margin: 0,
        paddingTop: 16,
        paddingBottom: 0,
    },
};

const StyledRadioSelect = styled(RadioSelect)({
    display: 'flex',
    flexDirection: 'column',
    ...labelStyles,
});

const Question = styled(BodyText1)({
    paddingTop: 16,
});

const StyledCheckboxContainer = styled(FlexColumn)({
    ...labelStyles,
});

const StyledTextArea = styled(InputComment)`
    font-size: 1em;
    color: rgb(22, 25, 28);
    background: rgb(240, 242, 241);
    border-radius: 4px;
    line-height: 30px;
    width: 100%;
    max-width: 100%;
    border: none;
    padding: 10px 20px;
    margin-left: 0;
    margin-top: 16px;
`;

const TEXT_FIELD_MAX_LENGTH = 128;

function selectedAsArray(selected: Answer['selected']): number[] {
    return Array.isArray(selected) ? selected : selected != null ? [selected] : [];
}

const SurveyQuestion: FC<Props> = React.memo(({ question, answer, onChange }) => {
    const [pasteWarnings, setPasteWarnings] = useState<Record<number, boolean>>({});
    const selected = selectedAsArray(answer.selected);

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const pasted = e.clipboardData.getData('text');
            const name = e.currentTarget.name;
            const id = parseInt(name);
            const existing = answer.comments?.[id] || '';
            if (pasted.length + existing.length > TEXT_FIELD_MAX_LENGTH) {
                setPasteWarnings((prev) => ({ ...prev, [id]: true }));
            }
        },
        [answer.comments]
    );
    const handleChange = useCallback(
        (e) => {
            switch (question.type) {
                case 'single':
                    return onChange({ ...answer, selected: parseInt(e.target.value) });
                case 'multi': {
                    const {
                        target: { checked, name },
                    } = e;
                    const id = parseInt(name);
                    const prev = selected;
                    const next = checked
                        ? [...prev, id]
                        : prev.filter((checkedId) => checkedId !== id);
                    return onChange({ ...answer, selected: next });
                }
                case 'text':
                    return onChange({ comment: e.target.value });
            }
        },
        [onChange, answer, question]
    );

    const handleCommentChange = useCallback(
        (e) => {
            const { value: comment, name } = e.target;
            const id = parseInt(name);

            return onChange({
                ...answer,
                comments: {
                    ...answer.comments,
                    [id]: comment,
                },
            });
        },
        [answer]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const id = parseInt(e.currentTarget.name);
            setPasteWarnings((prev) => {
                const { [id]: _, ...rest } = prev;
                return rest;
            });
        },
        []
    );

    let OptionsComponent = null;

    switch (question.type) {
        case 'single':
            OptionsComponent = (
                <StyledRadioSelect
                    name={question.id}
                    options={question.options.map((o) => ({
                        value: o.id,
                        label: o.answer,
                        commentable: o.commentable,
                    }))}
                    value={answer.selected}
                    onChange={handleChange}
                    comments={answer.comments}
                    onCommentChange={handleCommentChange}
                    onPaste={handlePaste}
                    pasteWarnings={pasteWarnings}
                    onBlur={handleBlur}
                />
            );
            break;
        case 'multi':
            OptionsComponent = (
                <StyledCheckboxContainer>
                    {question.options.map((o) => (
                        <Checkbox
                            key={o.id}
                            name={o.id}
                            label={o.answer}
                            commentable={o.commentable}
                            onCommentChange={handleCommentChange}
                            comments={answer.comments}
                            checked={selected.includes(o.id)}
                            onChange={handleChange}
                            onPaste={handlePaste}
                            pasteWarnings={pasteWarnings}
                            onBlur={handleBlur}
                        />
                    ))}
                </StyledCheckboxContainer>
            );
            break;
        case 'text':
            OptionsComponent = (
                <StyledTextArea
                    name={question.id}
                    value={answer.comment || ''}
                    onChange={handleChange}
                    rows={4}
                    onPaste={handlePaste}
                    showWarning={pasteWarnings?.[question.id]}
                    onBlur={handleBlur}
                    as="textarea"
                />
            );
            break;
        default:
            OptionsComponent = null;
            break;
    }

    return (
        <FlexColumn>
            <Question>{question.question}</Question>
            {OptionsComponent}
        </FlexColumn>
    );
});

export default SurveyQuestion;
